// ─── Shared document layout configuration ────────────────────────────────────
// Single source of truth for the printed-page geometry and typography used by
// BOTH the TipTap editor page (CSS custom properties + the pagination
// extension) and — numerically mirrored — the LaTeX preamble the backend
// builds in app.py (_latex_preamble). If a value changes here, change its
// LaTeX counterpart there too; each entry documents the mapping.
//
// The page is A4 with 20mm margins because that is what the compiled PDF uses
// (\geometry{a4paper,total={170mm,257mm},left=20mm,top=20mm}). The editor page
// deliberately renders at the same physical size (CSS 96dpi reference pixel)
// so page 1 in the editor ends where page 1 ends in the compiled PDF.

export const DOC_LAYOUT = {
  /** A4 paper — ↔ \geometry{a4paper} */
  pageWidthMm: 210,
  pageHeightMm: 297,
  /** Uniform 20mm margins — ↔ left=20mm, top=20mm, total={170mm,257mm} */
  pageMarginMm: 20,

  /**
   * ↔ \usepackage{mathptmx} — which IS URW Nimbus Roman. The editor page canvas
   * uses the very same font (embedded as a webfont in styles.scss) so it wraps
   * text exactly like the compiled PDF and their page breaks align. Falls back
   * to Times metrics only if the webfont fails to load.
   */
  fontFamily: "'Nimbus Roman', 'Times New Roman', Times, serif",
  /** ↔ \documentclass[12pt]{article} */
  fontSizePt: 12,
  /**
   * CSS line-height 1.5 → 18pt line boxes.
   * ↔ \linespread{1.25} (LaTeX default \baselineskip is 1.2 × font size, so
   *   1.25 × 1.2 × 12pt = 18pt baselines — identical vertical rhythm).
   */
  lineHeight: 1.5,
  /**
   * Space after each paragraph, in pt (0.5em at 12pt).
   * ↔ \setlength{\parskip}{6pt} + \setlength{\parindent}{0pt}
   */
  paragraphSpacingPt: 6,

  /** Table metrics — ↔ \tabcolsep=4.5pt (6px), \arrayrulewidth=0.75pt (1px) */
  tableCellPaddingH_Px: 6,
  tableCellPaddingV_Px: 2,
  tableBorderPx: 1,
} as const;

/** CSS reference pixel: 96px per inch, 25.4mm per inch. */
export const MM_TO_PX = 96 / 25.4;

export const PAGE_WIDTH_PX = Math.round(DOC_LAYOUT.pageWidthMm * MM_TO_PX);    // 794
export const PAGE_HEIGHT_PX = Math.round(DOC_LAYOUT.pageHeightMm * MM_TO_PX);  // 1123
export const PAGE_MARGIN_PX = Math.round(DOC_LAYOUT.pageMarginMm * MM_TO_PX);  // 76

/** Printable area — ↔ total={170mm,257mm} in the LaTeX geometry. */
export const PAGE_CONTENT_WIDTH_PX = PAGE_WIDTH_PX - 2 * PAGE_MARGIN_PX;   // 642
export const PAGE_CONTENT_HEIGHT_PX = PAGE_HEIGHT_PX - 2 * PAGE_MARGIN_PX; // 971

/**
 * Publishes the layout as CSS custom properties on `host` so the component
 * stylesheet derives every page dimension from the same constants the
 * pagination extension measures against — no duplicated magic numbers.
 */
export function applyDocLayoutCssVars(host: HTMLElement): void {
  host.style.setProperty('--doc-page-width', `${PAGE_WIDTH_PX}px`);
  host.style.setProperty('--doc-page-height', `${PAGE_HEIGHT_PX}px`);
  host.style.setProperty('--doc-page-margin', `${PAGE_MARGIN_PX}px`);
  host.style.setProperty('--doc-content-width', `${PAGE_CONTENT_WIDTH_PX}px`);
  host.style.setProperty('--doc-content-height', `${PAGE_CONTENT_HEIGHT_PX}px`);
  host.style.setProperty('--doc-font-family', DOC_LAYOUT.fontFamily);
  host.style.setProperty('--doc-font-size', `${DOC_LAYOUT.fontSizePt}pt`);
  host.style.setProperty('--doc-line-height', `${DOC_LAYOUT.lineHeight}`);
  host.style.setProperty('--doc-paragraph-spacing', `${DOC_LAYOUT.paragraphSpacingPt}pt`);
}
